import { useCallback, useEffect, useRef, useState } from "react";

import i18next from "i18next";
import { IoArrowBack, IoCheckmarkDoneOutline, IoDocumentAttachOutline, IoRefreshOutline } from "react-icons/io5";
import { useNavigate, useParams } from "react-router-dom";

import { useSupport } from "../../../context/support";
import {
  supportDownloadAttachment,
  supportTicket,
  supportTicketReply,
  supportTicketStatus,
} from "../../../protocols/rest/support";
import {
  formatFileSize,
  formatSupportDate,
  getSupportErrorKey,
  isSupportAccessError,
  supportCategoryLabel,
  supportErrorText,
  supportStatusLabel,
  validateSupportFiles,
} from "../utils";

import { ContainerComponent, DotsLoadingComponent, TextareaComponent } from "../../ui";
import SupportFilesInput from "../SupportFilesInput";
import SupportMessageText from "../SupportMessageText";
import SupportStatusBadge from "../SupportStatusBadge";

import {
  AttachmentButton,
  Attachments,
  Bubble,
  ButtonLoadingCover,
  Buttons,
  Card,
  CardTitle,
  Chat,
  ChatHeader,
  FormError,
  InfoGrid,
  Loading,
  MessageAuthor,
  MessageRow,
  MessageTime,
  NarrowCard,
  Notice,
  PageWrapper,
  PrimaryButton,
  ReplyActions,
  ReplyBox,
  SecondaryButton,
  SystemEvent,
  TextButton,
  TextLink,
} from "../style";

const systemEventText = message => {
  const { t } = i18next;

  if (message.event === "status_changed" && message.meta) {
    return t("support_event_status", {
      from: supportStatusLabel(message.meta.from),
      to: supportStatusLabel(message.meta.to),
    });
  }

  if (message.event === "created") return t("support_event_created");

  return message.body || "";
};

/**
 * /support/:id — переписка по заявке: мои сообщения справа, «Поддержка» слева, системные события по центру.
 * Без входа и без гостевого доступа — на логин и обратно на эту заявку.
 */
const SupportTicketView = () => {
  const { t } = i18next;
  const { id } = useParams();
  const navigate = useNavigate();
  const { mode, meta, revision, lastEvent, refreshSummary } = useSupport();

  const [ticket, setTicket] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [filesError, setFilesError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const chatRef = useRef(null);
  const limits = meta?.limits || {};

  const loginRedirect = `/auth?redirect=${encodeURIComponent(`/support/${id}`)}`;

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!mode) return;

      if (!silent) setIsLoading(true);

      try {
        const data = await supportTicket(id);

        setTicket(data?.ticket || null);
        setLoadError(null);
        // заявка открыта — непрочитанные сброшены на сервере, обновляем счётчик в шапке
        refreshSummary();
      } catch (error) {
        if (isSupportAccessError(error) && mode === "user") {
          navigate(loginRedirect, { replace: true });
          return;
        }

        if (!silent) setLoadError(error);
      } finally {
        setIsLoading(false);
      }
    },
    [id, mode, navigate, loginRedirect, refreshSummary]
  );

  // ни входа, ни гостевого доступа — на логин и обратно
  useEffect(() => {
    if (!mode) {
      navigate(loginRedirect, { replace: true });
      return;
    }

    load();
  }, [mode, load, navigate, loginRedirect]);

  // событие по этой заявке (или изменения, найденные опросом) — тихо перечитываем
  const isFirstRevision = useRef(true);
  useEffect(() => {
    if (isFirstRevision.current) {
      isFirstRevision.current = false;
      return;
    }

    if (!lastEvent?.ticket_id || String(lastEvent.ticket_id) === String(id)) load({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision]);

  const messagesCount = ticket?.messages?.length || 0;

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messagesCount]);

  const sendReply = async () => {
    if (isSending) return;

    const text = message.trim();
    const messageMax = Number(limits.message_max) || 10000;

    if (!text) {
      setActionError(t("required"));
      return;
    }

    if (text.length > messageMax) {
      setActionError(t("support_text_too_long", { max: messageMax }));
      return;
    }

    const fileError = validateSupportFiles(files, limits);
    setFilesError(fileError);
    if (fileError) return;

    setIsSending(true);
    setActionError(null);

    try {
      const data = await supportTicketReply({ ticket_id: ticket.id, message: text, files });

      if (data?.ticket) setTicket(data.ticket);
      setMessage("");
      setFiles([]);
    } catch (error) {
      if (getSupportErrorKey(error) === "support_file_invalid") {
        setFilesError(supportErrorText(error));
      } else {
        setActionError(supportErrorText(error));
      }

      // заявку успели закрыть — показываем актуальное состояние с кнопкой «Переоткрыть»
      if (getSupportErrorKey(error) === "support_ticket_closed") load({ silent: true });
    } finally {
      setIsSending(false);
    }
  };

  const changeStatus = async status => {
    if (isChangingStatus) return;
    if (status === "closed" && !window.confirm(t("support_close_confirm"))) return;

    setIsChangingStatus(true);
    setActionError(null);

    try {
      const data = await supportTicketStatus({ ticket_id: ticket.id, status });

      if (data?.ticket) setTicket(data.ticket);
      refreshSummary();
    } catch (error) {
      setActionError(supportErrorText(error));
      load({ silent: true });
    } finally {
      setIsChangingStatus(false);
    }
  };

  const download = async attachment => {
    setDownloadingId(attachment.id);
    setActionError(null);

    try {
      await supportDownloadAttachment({ ticket_id: ticket.id, attachment_id: attachment.id, name: attachment.name });
    } catch (error) {
      setActionError(supportErrorText(error));
    } finally {
      setDownloadingId(null);
    }
  };

  if (!mode) return null;

  if (isLoading && !ticket) {
    return (
      <PageWrapper>
        <ContainerComponent>
          <Loading style={{ minHeight: 300 }}>
            <DotsLoadingComponent />
          </Loading>
        </ContainerComponent>
      </PageWrapper>
    );
  }

  if (!ticket) {
    return (
      <PageWrapper>
        <ContainerComponent>
          <NarrowCard>
            <CardTitle>{t("support_nav")}</CardTitle>
            <FormError>{supportErrorText(loadError)}</FormError>
            <Buttons>
              <TextLink to="/support">
                <IoArrowBack />
                {t("support_back_to_list")}
              </TextLink>
              {mode === "guest" && <TextLink to={loginRedirect}>{t("support_login")}</TextLink>}
            </Buttons>
          </NarrowCard>
        </ContainerComponent>
      </PageWrapper>
    );
  }

  const isClosed = ticket.status === "closed";

  return (
    <PageWrapper>
      <ContainerComponent>
        <div style={{ paddingTop: 20, paddingBottom: 12 }}>
          <TextLink to="/support">
            <IoArrowBack />
            {t("support_back_to_list")}
          </TextLink>
        </div>

        <Card>
          <ChatHeader>
            <InfoGrid>
              <span>{t("support_ticket_number", { number: ticket.number })}</span>
              <SupportStatusBadge status={ticket.status} />
            </InfoGrid>
            <CardTitle>{ticket.subject}</CardTitle>
            <InfoGrid>
              {ticket.order_id && (
                <span>
                  {t("support_order")}: <b>{ticket.order_id}</b>
                </span>
              )}
              {ticket.category && (
                <span>
                  {t("support_category")}: <b>{supportCategoryLabel(ticket.category)}</b>
                </span>
              )}
              <span>
                {t("support_created_at")}: <b>{formatSupportDate(ticket.created_at)}</b>
              </span>
            </InfoGrid>
          </ChatHeader>

          {ticket.status === "waiting_customer" && <Notice $variant="warning">{t("support_waiting_banner")}</Notice>}

          <Chat ref={chatRef}>
            {(ticket.messages || []).map(item => {
              if (item.author_type === "system" || (!item.body && item.event)) {
                return <SystemEvent key={item.id}>{systemEventText(item)} · {formatSupportDate(item.created_at)}</SystemEvent>;
              }

              const isMine = !!item.is_mine;
              const isStaff = item.author_type === "staff";
              const author = isMine
                ? t("support_author_you")
                : isStaff
                  ? t("support_author_staff")
                  : item.author_name || t("support_author_you");

              return (
                <MessageRow key={item.id} $mine={isMine}>
                  <Bubble $mine={isMine}>
                    <MessageAuthor $staff={isStaff}>{author}</MessageAuthor>
                    <SupportMessageText text={item.body} />
                    {item.attachments?.length > 0 && (
                      <Attachments>
                        {item.attachments.map(attachment => (
                          <AttachmentButton
                            key={attachment.id}
                            type="button"
                            onClick={() => download(attachment)}
                            disabled={downloadingId === attachment.id}
                            title={t("support_download")}
                          >
                            <IoDocumentAttachOutline />
                            <span>{attachment.name}</span>
                            <small>{formatFileSize(attachment.size)}</small>
                          </AttachmentButton>
                        ))}
                      </Attachments>
                    )}
                    <MessageTime $mine={isMine}>{formatSupportDate(item.created_at)}</MessageTime>
                  </Bubble>
                </MessageRow>
              );
            })}
          </Chat>

          {isClosed && (
            <Notice $variant="muted">
              <span style={{ flex: 1 }}>
                {ticket.can_reopen ? t("support_closed_banner") : t("support_closed_banner_no_reopen")}
              </span>
              {ticket.can_reopen && (
                <SecondaryButton type="button" onClick={() => changeStatus("in_progress")} disabled={isChangingStatus}>
                  <IoRefreshOutline />
                  {t("support_reopen")}
                </SecondaryButton>
              )}
            </Notice>
          )}

          {actionError && <FormError>{actionError}</FormError>}

          {ticket.can_reply && !isClosed && (
            <ReplyBox>
              <TextareaComponent
                value={message}
                onChange={setMessage}
                placeholder={t("support_reply_placeholder")}
                rows={3}
                maxLength={Number(limits.message_max) || 10000}
                disabled={isSending}
              />
              <SupportFilesInput
                files={files}
                onChange={nextFiles => {
                  setFiles(nextFiles);
                  setFilesError(validateSupportFiles(nextFiles, limits));
                }}
                limits={limits}
                error={filesError}
                disabled={isSending}
              />
              <ReplyActions>
                <PrimaryButton type="button" onClick={sendReply} disabled={isSending}>
                  {t("support_send")}
                  {isSending && (
                    <ButtonLoadingCover>
                      <DotsLoadingComponent color="#fff" />
                    </ButtonLoadingCover>
                  )}
                </PrimaryButton>
                {ticket.can_close && (
                  <TextButton type="button" onClick={() => changeStatus("closed")} disabled={isChangingStatus}>
                    <IoCheckmarkDoneOutline />
                    {t("support_close")}
                  </TextButton>
                )}
              </ReplyActions>
            </ReplyBox>
          )}

          {!ticket.can_reply && !isClosed && ticket.can_close && (
            <Buttons>
              <TextButton type="button" onClick={() => changeStatus("closed")} disabled={isChangingStatus}>
                <IoCheckmarkDoneOutline />
                {t("support_close")}
              </TextButton>
            </Buttons>
          )}
        </Card>
      </ContainerComponent>
    </PageWrapper>
  );
};

export default SupportTicketView;
